import { msg } from "@lingui/core/macro";
import {
  type Component,
  type ComponentProps,
  createEffect,
  Show,
  Suspense,
  untrack,
} from "solid-js";
import type { getMyDayTodosCountQuery, getTodosCountQuery, ListWithMeta } from "@listto/shared";
import { Button } from "@/components/ui/button";
import { CheckSquare, Settings } from "lucide-solid";
import { useI18n } from "@/providers/i18n-provider";
import type { PowerSyncDrizzleDB, Todo } from "@listto/powersync";
import { TaskCountBadge } from "./task-count-badge";
import { createRovingFocus } from "@/lib/navigation/solid";

// Header controls form a small toolbar; arrow/vim keys rove between them and
// ArrowDown/j off the last control hands focus to the first todo row.
const HEADER_NAV_KEYS = new Set([
  "ArrowUp",
  "ArrowDown",
  "ArrowLeft",
  "ArrowRight",
  "h",
  "j",
  "k",
  "l",
  "Home",
  "End",
]);

interface TodoListMainHeaderProps {
  /** The real list object — undefined for dashboard views (My Day, Important, etc.) */
  currentList: ListWithMeta | undefined;
  title: string;
  icon: Component<ComponentProps<"svg">>;
  // Title editing (only for real lists)
  isEditingTitle: boolean;
  tempTitle: string;
  onTempTitleInput: (value: string) => void;
  onStartEditTitle: () => void;
  onSaveTitle: () => void;
  onCancelEditTitle: () => void;
  // Selection row (only for real lists)
  isSelectionMode: boolean;
  currentListTodos: Todo[] | undefined;
  onToggleSelection: () => void;
  onOpenSettings: () => void;
  listId: string;
  userId: string | undefined;
  countQueryFn: (
    db: PowerSyncDrizzleDB,
    userId: string,
  ) => ReturnType<typeof getTodosCountQuery> | ReturnType<typeof getMyDayTodosCountQuery>;
  /** Forwarded ref so the parent can measure the header height for virtualization */
  ref?: (el: HTMLDivElement) => void;
  /** Hand keyboard focus to the first todo row (ArrowDown/j off the last control). */
  onEnterList?: () => void;
}

export function TodoListMainHeader(props: TodoListMainHeaderProps) {
  const { t } = useI18n();

  // Roving toolbar over the header controls (icon → title → select → settings,
  // in DOM order). manageTabIndex is off so each control stays an independent
  // Tab stop; the roving core only drives arrow/vim movement and DOM focus.
  const headerNav = createRovingFocus({
    orientation: "both",
    vimKeys: true,
    wrap: false,
    manageTabIndex: false,
  });

  const onControlFocus = (id: string) => () => {
    headerNav.setActive(id, { focus: false });
  };

  const handleHeaderKeyDown = (event: KeyboardEvent) => {
    if (!HEADER_NAV_KEYS.has(event.key)) {
      return;
    }
    // Own every nav key so the global list listener never double-handles it.
    event.stopPropagation();
    if (headerNav.handleKeyDown(event)) {
      return;
    }
    // At an edge: ArrowDown/j off the last control enters the list; otherwise stay.
    event.preventDefault();
    if (event.key === "ArrowDown" || event.key === "j") {
      props.onEnterList?.();
    }
  };

  return (
    <div
      ref={props.ref}
      class="flex flex-col gap-2 mb-3 group/main-title animate-in fade-in slide-in-from-top-2 duration-300"
    >
      {/* Row 1: Icon + Title */}
      <div class="flex items-center gap-4">
        {/* Icon — editable button for real lists, read-only div for dashboard views */}
        <Show
          when={props.currentList}
          fallback={
            <div class="p-2 bg-primary/10 rounded-xl shrink-0">
              <props.icon class="h-8 w-8 text-primary" />
            </div>
          }
        >
          {(list) => (
            <button
              id="list-icon-btn"
              ref={headerNav.item("list-icon-btn")}
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                props.onOpenSettings();
              }}
              onFocus={onControlFocus("list-icon-btn")}
              onKeyDown={handleHeaderKeyDown}
              class="bg-transparent border-0 cursor-pointer hover:scale-105 active:scale-95 transition-transform p-0 rounded-xl outline-none focus-visible:ring-2 focus-visible:ring-primary/40 shrink-0"
              title={t(msg`Edit list icon`)}
            >
              <Show
                when={list().icon}
                fallback={
                  <div class="p-2 bg-primary/10 rounded-xl">
                    <props.icon class="h-8 w-8 text-primary shrink-0" />
                  </div>
                }
              >
                {(iconEmoji) => (
                  <span class="text-4xl shrink-0 leading-none select-none block p-0.5">
                    {iconEmoji()}
                  </span>
                )}
              </Show>
            </button>
          )}
        </Show>

        {/* Title */}
        <div class="flex-1 min-w-0">
          <Show
            when={props.currentList && props.isEditingTitle}
            fallback={
              <Show
                when={props.currentList}
                fallback={
                  // Dashboard view: plain non-interactive heading
                  <h1 class="text-xl sm:text-2xl font-extrabold tracking-tight break-words leading-tight text-foreground/90">
                    {props.title}
                  </h1>
                }
              >
                {/* Real list: clickable heading that activates inline edit */}
                <button
                  id="list-title-edit-btn"
                  ref={headerNav.item("list-title-edit-btn")}
                  type="button"
                  class="text-left bg-transparent border-0 cursor-pointer hover:opacity-85 active:opacity-60 transition-opacity p-0 rounded outline-none focus-visible:ring-2 focus-visible:ring-primary/40 font-inherit text-foreground min-w-0 w-full"
                  onClick={props.onStartEditTitle}
                  onFocus={onControlFocus("list-title-edit-btn")}
                  onKeyDown={handleHeaderKeyDown}
                  title={t(msg`Click to edit list name`)}
                >
                  <h1 class="text-xl sm:text-2xl font-extrabold tracking-tight break-words leading-tight text-foreground/90 border-b-2 border-transparent py-0.5">
                    {props.title}
                  </h1>
                </button>
              </Show>
            }
          >
            {/* Inline title input - editable h1 prevents shifting or single line layout restriction */}
            <div class="animate-in fade-in zoom-in-98 duration-200 pr-2">
              <h1
                contentEditable
                ref={(el) => {
                  createEffect(() => {
                    const value = props.tempTitle;
                    if (el.textContent !== value) {
                      el.textContent = value;
                    }
                  });
                  setTimeout(() => {
                    el.focus();
                    // Select all text in contenteditable
                    const range = document.createRange();
                    range.selectNodeContents(el);
                    const sel = globalThis.getSelection();
                    if (sel) {
                      sel.removeAllRanges();
                      sel.addRange(range);
                    }
                  }, 0);
                }}
                onInput={(e) => {
                  props.onTempTitleInput(e.currentTarget.textContent || "");
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    props.onSaveTitle();
                  } else if (e.key === "Escape") {
                    props.onCancelEditTitle();
                  }
                }}
                onBlur={() => {
                  props.onSaveTitle();
                }}
                class="bg-transparent border-b-2 border-primary text-xl sm:text-2xl font-extrabold outline-none px-0 py-0.5 w-full text-foreground transition-all duration-300 tracking-tight break-words leading-tight text-foreground/90"
              >
                {untrack(() => props.tempTitle)}
              </h1>
            </div>
          </Show>
        </div>
      </div>

      {/* Row 2: Select + Count + Settings — only for real lists */}
      <Show when={props.currentList}>
        <div class="flex items-center justify-between border-b border-border/40 pb-2 mt-1 select-none">
          <div class="flex items-center gap-3">
            <Button
              id="list-select-btn"
              ref={headerNav.item("list-select-btn")}
              variant="outline"
              size="sm"
              class="h-8 gap-1.5 text-xs rounded-lg hover:bg-muted/50 focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:outline-none focus:scale-100 focus:brightness-100"
              onClick={props.onToggleSelection}
              onFocus={onControlFocus("list-select-btn")}
              onKeyDown={handleHeaderKeyDown}
            >
              <CheckSquare class="h-3.5 w-3.5" />
              {props.isSelectionMode ? t(msg`Cancel`) : t(msg`Select`)}
            </Button>

            <span class="inline-flex items-center rounded-full bg-muted/60 px-2.5 py-0.5 text-xs font-semibold text-muted-foreground">
              <Suspense fallback={<span>0 {t(msg`tasks`)}</span>}>
                <TaskCountBadge
                  listId={props.listId}
                  userId={props.userId}
                  countQueryFn={props.countQueryFn}
                />
              </Suspense>
            </span>
          </div>

          <button
            id="list-settings-btn"
            ref={headerNav.item("list-settings-btn")}
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              props.onOpenSettings();
            }}
            onFocus={onControlFocus("list-settings-btn")}
            onKeyDown={handleHeaderKeyDown}
            class="p-2 rounded-lg hover:bg-muted/40 transition-all text-muted-foreground hover:text-foreground shrink-0 cursor-pointer outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
            title={t(msg`Edit list settings`)}
          >
            <Settings class="h-4.5 w-4.5" />
          </button>
        </div>
      </Show>

      {/* Dashboard views: a subtle separator instead of the settings row */}
      <Show when={!props.currentList}>
        <div class="border-b border-border/40 mb-1" />
      </Show>
    </div>
  );
}
